import { Input, Label, Textarea } from '@quickmd-llc/design-system'
import { ServiceImageUpload } from './ServiceImageUpload'
import { SHOW_SERVICE_IMAGE_UPLOAD } from '../../../../constants/unreleasedServiceFeatures'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SERVICE_NAME_MAX = 70
const ABBREVIATION_MAX = 4
const DESCRIPTION_MAX = 500

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  serviceName: string
  onSetServiceName: (value: string) => void
  serviceNameError: string
  serviceAbbreviation: string
  onSetServiceAbbreviation: (value: string) => void
  serviceAbbreviationError: string
  description: string
  onDescriptionChange: (value: string) => void
  descriptionLocalError: string
  isDescriptionError: boolean
  imageUrl?: string
  onImageChange?: (file: File) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ServiceInfoForm = ({
  serviceName,
  onSetServiceName,
  serviceNameError,
  serviceAbbreviation,
  onSetServiceAbbreviation,
  serviceAbbreviationError,
  description,
  onDescriptionChange,
  descriptionLocalError,
  isDescriptionError,
  imageUrl,
  onImageChange,
}: Props) => (
  /*
   * SCSS → Tailwind mapping notes:
   * .sif-layout: flex gap-sp-3 items-start
   * .sif-form: flex-1 flex flex-col gap-sp-2
   * .sif-name-row: grid [grid-template-columns:3fr_1fr] gap-sp-2 items-start
   * .sif-field: flex flex-col gap-sp-0.5
   * .sif-label: block mb-sp-0.5
   * .sif-field-footer: flex items-center justify-between min-h-[16px]
   * .sif-error: text-text-error  (SCSS #d94444 → closest DS token is text-text-error = red-70;
   *             note DS red-50=#CB4D25 is closer in lightness but red-70 is the canonical error token)
   * .sif-char-count: text-size-2 font-normal leading-4 text-text-light ml-auto
   *
   * ServiceImageUpload (.siu-*) classes have been converted to Tailwind in ServiceImageUpload.tsx.
   */
  <div className='flex items-start gap-sp-3'>
    {SHOW_SERVICE_IMAGE_UPLOAD ?
      <ServiceImageUpload imageUrl={imageUrl ?? ''} onImageChange={onImageChange ?? (() => {})} />
    : null}
    <div className='flex flex-1 flex-col gap-sp-2'>
      <div className='grid items-start gap-sp-2 [grid-template-columns:3fr_1fr]'>
        <div className='flex flex-col gap-sp-0.5'>
          <Label size='md' className='mb-sp-0.5 block'>
            Service name
          </Label>
          <Input
            data-testid='pw_sif-service-name'
            value={serviceName}
            onChange={(e) => onSetServiceName(e.target.value)}
            placeholder='Enter service name'
            error={!!serviceNameError}
          />
          <div className='flex min-h-[16px] items-center justify-between'>
            {serviceNameError ?
              <Label size='sm' className='text-text-error'>
                {serviceNameError}
              </Label>
            : <span />}
            <span className='ml-auto text-size-2 font-normal leading-4 text-text-light'>
              {serviceName.length} / {SERVICE_NAME_MAX}
            </span>
          </div>
        </div>
        <div className='flex flex-col gap-sp-0.5'>
          <Label size='md' className='mb-sp-0.5 block'>
            Service abbreviation
          </Label>
          <Input
            data-testid='pw_sif-service-abbr'
            value={serviceAbbreviation}
            onChange={(e) => onSetServiceAbbreviation(e.target.value)}
            placeholder='Enter abbreviation'
            error={!!serviceAbbreviationError}
          />
          <div className='flex min-h-[16px] items-center justify-between'>
            {serviceAbbreviationError ?
              <Label size='sm' className='text-text-error'>
                {serviceAbbreviationError}
              </Label>
            : <span />}
            <span className='ml-auto text-size-2 font-normal leading-4 text-text-light'>
              {serviceAbbreviation.length} / {ABBREVIATION_MAX}
            </span>
          </div>
        </div>
      </div>
      <div className='flex flex-col gap-sp-0.5'>
        <Label size='md' className='mb-sp-0.5 block'>
          Description
        </Label>
        <Textarea
          data-testid='pw_sif-description'
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder='Type your message...'
          rows={6}
          error={isDescriptionError}
        />
        <div className='flex min-h-[16px] items-center justify-between'>
          {descriptionLocalError ?
            <Label size='sm' className='text-text-error'>
              {descriptionLocalError}
            </Label>
          : <span />}
          <span className='ml-auto text-size-2 font-normal leading-4 text-text-light'>
            {description.length} / {DESCRIPTION_MAX}
          </span>
        </div>
      </div>
    </div>
  </div>
)
